/**
 * PreCreditDepositDialog — buyer-facing deposit to per-seller pre-credit pool.
 *
 * Buyer drops cash from their wallet into a pool earmarked for a specific
 * seller. Non-refundable per founder spec; redeems against future invoices
 * automatically (highest-priority payment method). SMS step-up gated.
 *
 * Caller:
 *   <PreCreditDepositDialog
 *      open={open} onClose={…}
 *      seller={{ id, name }}    // required
 *      walletBalance={wallet.balance}
 *      onSuccess={() => reload()}
 *   />
 */
import { useEffect, useMemo, useState } from "react";
import { Landmark, Loader2, Wallet, AlertTriangle, Info } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import api, { formatErr, money } from "../lib/api";
import PaymentApprovalModal from "./PaymentApprovalModal";

export default function PreCreditDepositDialog({ open, onClose, seller, walletBalance = 0, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [note, setNote]     = useState("");
  const [busy, setBusy]     = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setAmount(""); setNote(""); setBusy(false); setApprovalOpen(false);
    }
  }, [open]);

  const amountNum = Number(amount) || 0;
  const overWallet = amountNum > walletBalance;
  const canSubmit = amountNum > 0 && !overWallet && !busy && !!seller?.id;

  const submitWithApproval = async (approval_token) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/sellers/${seller.id}/pre-credit/deposit`,
        { amount: amountNum, note: note || undefined, approval_token },
      );
      toast.success(`Deposited ${money(amountNum)} to ${data.seller_name || seller.name}'s pre-credit pool`);
      onSuccess?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || formatErr(detail) || "Deposit failed");
    } finally { setBusy(false); }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setApprovalOpen(true);
  };

  if (!seller) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-md" data-testid="pre-credit-deposit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-4 h-4"/>
              Deposit pre-credit
            </DialogTitle>
            <div className="text-[12px] text-foreground/60 mt-1">
              For future orders with <span className="font-medium text-foreground/80">{seller.name}</span>
            </div>
          </DialogHeader>

          <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2.5 text-[12px] text-sky-900 leading-relaxed flex items-start gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
            <div>
              <strong>Locked with this seller.</strong> Pre-credit auto-applies
              to your next invoice from <span className="font-medium">{seller.name}</span>,
              before card, wallet, or ACH. Refundable only if the seller issues
              a credit memo against this deposit — it goes back to your wallet.
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[12px] mb-1.5 block">Amount (USD)</Label>
              <Input
                type="number" min="0.01" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-12 display text-2xl"
                data-testid="pc-amount"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[500, 2500, 10000, 50000].filter(p => p <= walletBalance).map(p => (
                  <button key={p} type="button"
                    onClick={() => setAmount(String(p))}
                    className="text-xs rounded-full border border-border px-3 py-1.5 hover:border-foreground/40"
                    data-testid={`pc-preset-${p}`}>
                    {money(p)}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2 text-[11px] text-foreground/55">
                <span className="inline-flex items-center gap-1.5">
                  <Wallet className="w-3 h-3"/>
                  Wallet balance: <span className="font-medium text-foreground/75">{money(walletBalance)}</span>
                </span>
                {walletBalance > 0 && (
                  <button type="button"
                    onClick={() => setAmount(String(walletBalance))}
                    className="text-foreground/65 underline hover:text-foreground"
                    data-testid="pc-amount-max">
                    Use all
                  </button>
                )}
              </div>
              {overWallet && (
                <div className="mt-1.5 text-[11.5px] text-rose-700 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3"/>
                  Wallet has only {money(walletBalance)} — top up first or pick less.
                </div>
              )}
            </div>

            <div>
              <Label className="text-[12px] mb-1.5 block">
                Note <span className="text-foreground/45 ml-1 text-[11px]">(optional · 240 chars)</span>
              </Label>
              <Textarea
                rows={2} maxLength={240}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Q3 inventory deposit · PO #4501"
                data-testid="pc-note"
              />
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-border">
              <div className="text-[12px] text-foreground/65 inline-flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5"/>
                Owner SMS approval required
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-full" onClick={onClose} data-testid="pc-cancel">
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSubmit} data-testid="pc-submit"
                        className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                  {busy
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Depositing…</>
                    : <>Deposit {amountNum > 0 ? money(amountNum) : ""}</>}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PaymentApprovalModal
        open={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        action="deposit_pre_credit"
        amount={amountNum}
        title={`Approve ${money(amountNum)} pre-credit deposit`}
        description={`Locks ${money(amountNum)} from your wallet into ${seller.name}'s pre-credit pool. Refundable via seller credit memo.`}
        onApproved={submitWithApproval}
      />
    </>
  );
}
